import { addYears, endOfYear, format, isValid, parse } from "date-fns";
import { CalendarDays } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
    InputGroup,
    InputGroupAddon,
    InputGroupButton,
    InputGroupInput,
} from "@/components/ui/input-group";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Time slots (30-minute intervals, 24-hour format)
// ---------------------------------------------------------------------------

function buildTimeSlots(): string[] {
    const slots: string[] = [];
    for (let h = 0; h < 24; h++) {
        for (const m of [0, 30]) {
            const hh = String(h).padStart(2, "0");
            const mm = m === 0 ? "00" : "30";
            slots.push(`${hh}:${mm}`);
        }
    }
    return slots;
}

const TIME_SLOTS = buildTimeSlots();

const DEFAULT_DATE_FORMAT = "d MMM yyyy";

const ALLOWED_DATE_FORMATS = [
    DEFAULT_DATE_FORMAT,
    "d M yyyy",
    "d MMMM yyyy",
    "yyyy M d",
    "yyyy MMM d",
    "M d yyyy",
    "MMM d yyyy",
    "MMMM d yyyy",
    "d MMM",
    "d MMMM",
    "d M",
];

const TIME_REGEX = /^([01]?\d|2[0-3]):([0-5]\d)$/;

export function tenYearsEndMonth(from: Date = new Date()): Date {
    return endOfYear(addYears(from, 10));
}

function filterTimeSlots(query: string): string[] {
    if (!query) return TIME_SLOTS;
    const q = query.replace(":", "");
    return TIME_SLOTS.filter(
        (t) => t.startsWith(query) || t.replace(":", "").startsWith(q),
    );
}

// ---------------------------------------------------------------------------
// DatePickerInput
// ---------------------------------------------------------------------------

export const INVALID_DATE = new Date(NaN);

export interface DatePickerInputProps {
    date?: Date;
    onDateChange?: (date: Date | undefined) => void;
    /** Last month shown in the year menu. Omitted menus stop at the end of this year. */
    endMonth?: Date;
    placeholder?: string;
    className?: string;
    invalid?: boolean;
}

function parseDate(value: string): Date | undefined {
    const spaced = value.trim().replace(/[\s./,-]+/g, " ");

    for (const fmt of ALLOWED_DATE_FORMATS) {
        const parsed = parse(spaced, fmt, new Date());
        if (isValid(parsed)) return parsed;
    }

    return undefined;
}

export function DatePickerInput({
    date,
    onDateChange,
    endMonth,
    placeholder = "Date",
    className,
    invalid,
}: DatePickerInputProps) {
    const [open, setOpen] = useState(false);
    const fieldRef = useRef<HTMLDivElement>(null);
    // Show the year so the selected year is always visible
    const [inputValue, setInputValue] = useState(
        date && isValid(date) ? format(date, DEFAULT_DATE_FORMAT) : "",
    );
    const selected = parseDate(inputValue);

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const val = e.target.value;
        setInputValue(val);
        onDateChange?.(val.trim() ? (parseDate(val) ?? INVALID_DATE) : undefined);
    }

    function handleBlur() {
        if (selected) setInputValue(format(selected, DEFAULT_DATE_FORMAT));
    }

    function handleCalendarSelect(d: Date | undefined) {
        onDateChange?.(d);
        setInputValue(d ? format(d, DEFAULT_DATE_FORMAT) : "");
        setOpen(false);
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <InputGroup ref={fieldRef} className={className}>
                <InputGroupInput
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    aria-invalid={invalid || undefined}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") setOpen(false);
                        if (e.metaKey || e.ctrlKey) {
                            const k = e.key.toLowerCase();
                            if (["a", "c", "v", "x", "z", "y"].includes(k)) {
                                e.stopPropagation();
                                if (k === "a") e.currentTarget.select();
                            }
                        }
                    }}
                />
                <InputGroupAddon align="inline-end">
                    <InputGroupButton
                        size="icon-xs"
                        variant="ghost"
                        render={<PopoverTrigger />}
                    >
                        <CalendarDays className="pointer-events-none size-3.5 text-muted-foreground" />
                    </InputGroupButton>
                </InputGroupAddon>
            </InputGroup>
            <PopoverContent
                anchor={fieldRef}
                side="bottom"
                align="start"
                sideOffset={6}
                collisionAvoidance={{ side: "none", fallbackAxisSide: "none" }}
                className="w-auto p-0"
            >
                <Calendar
                    mode="single"
                    selected={selected}
                    defaultMonth={selected}
                    captionLayout="dropdown"
                    endMonth={endMonth}
                    onSelect={handleCalendarSelect}
                    autoFocus
                />
            </PopoverContent>
        </Popover>
    );
}

// ---------------------------------------------------------------------------
// TimePickerInput
// ---------------------------------------------------------------------------

export interface TimePickerInputProps {
    time?: string;
    onTimeChange?: (time: string) => void;
    placeholder?: string;
    className?: string;
}

export function TimePickerInput({
    time,
    onTimeChange,
    placeholder = "Time",
    className,
}: TimePickerInputProps) {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState(time ?? "");
    const groupRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setInputValue(time ?? "");
    }, [time]);

    const filteredSlots = filterTimeSlots(inputValue);

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const val = e.target.value;
        setInputValue(val);
        setOpen(true);
        if (TIME_REGEX.test(val)) {
            const [h, m] = val.split(":");
            onTimeChange?.(`${String(parseInt(h, 10)).padStart(2, "0")}:${m}`);
        }
    }

    function handleSlotSelect(t: string) {
        setInputValue(t);
        onTimeChange?.(t);
        setOpen(false);
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <InputGroup ref={groupRef} className={className}>
                <InputGroupInput
                    value={inputValue}
                    onChange={handleInputChange}
                    onClick={() => setOpen(true)}
                    placeholder={placeholder}
                    onKeyDown={(e) => {
                        if (e.key === "ArrowDown" && !open) {
                            setOpen(true);
                            e.preventDefault();
                        }
                        if (e.key === "Escape") {
                            setOpen(false);
                            e.currentTarget.blur();
                        }
                        if (e.metaKey || e.ctrlKey) {
                            const k = e.key.toLowerCase();
                            if (["a", "c", "v", "x", "z", "y"].includes(k)) {
                                e.stopPropagation();
                                if (k === "a") e.currentTarget.select();
                            }
                        }
                    }}
                />
            </InputGroup>
            <PopoverContent
                anchor={groupRef}
                align="start"
                sideOffset={6}
                className="w-32 p-1"
            >
                <div className="max-h-52 overflow-y-auto">
                    {filteredSlots.length > 0 ? (
                        filteredSlots.map((t) => (
                            <Button
                                key={t}
                                variant="ghost"
                                tabIndex={-1}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleSlotSelect(t)}
                                className={cn(
                                    "h-auto w-full justify-start rounded-sm px-2 py-1.5 text-left font-normal text-sm hover:bg-zinc-50",
                                    time === t
                                        ? "bg-zinc-100 font-medium text-zinc-900"
                                        : "text-zinc-700",
                                )}
                            >
                                {t}
                            </Button>
                        ))
                    ) : (
                        <p className="px-2 py-2 text-sm text-zinc-400">No matches</p>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

// ---------------------------------------------------------------------------
// DateTimePair — the composed date + time row
// ---------------------------------------------------------------------------

export interface DateTimePairProps {
    date?: Date;
    time?: string;
    onDateChange?: (date: Date | undefined) => void;
    onTimeChange?: (time: string) => void;
    datePlaceholder?: string;
    timePlaceholder?: string;
}

export function DateTimePair({
    date,
    time,
    onDateChange,
    onTimeChange,
    datePlaceholder,
    timePlaceholder,
}: DateTimePairProps) {
    const [localDate, setLocalDate] = useState<Date | undefined>(date);
    const [localTime, setLocalTime] = useState<string | undefined>(time);

    useEffect(() => {
        setLocalTime(time);
    }, [time]);

    function handleDateChange(d: Date | undefined) {
        setLocalDate(d);
        onDateChange?.(d);
    }

    function handleTimeChange(t: string) {
        setLocalTime(t);
        onTimeChange?.(t);
    }

    return (
        <div className="flex gap-1">
            <div className="min-w-0 flex-[2]">
                <DatePickerInput
                    date={localDate}
                    onDateChange={handleDateChange}
                    placeholder={datePlaceholder}
                />
            </div>
            <div className="min-w-0 flex-1">
                <TimePickerInput
                    time={localTime}
                    onTimeChange={handleTimeChange}
                    placeholder={timePlaceholder}
                />
            </div>
        </div>
    );
}
